import React from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import Button from "reactstrap/lib/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import FormGroup from "reactstrap/lib/FormGroup";
import Label from "reactstrap/lib/Label";
import Select from "react-select";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "../../services/api/api";
// Ren axios för auth-serverns anonyma endpoint, samma mönster som självregistreringen —
// api-instansens interceptors hör inte hemma på det anropet.
import authAxios from "axios";
import { AUTH_BASE_URL } from "../../constants/api.constants";
import { toast } from "react-toastify";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { PASSWORD_PATTERN, generatePassword } from "../../Utilities/passwordPolicy";
import { copyToClipboard } from "../../Utilities/clipboardHelper";

const roleLabelOf = (role) => role?.label ?? role;

const CreateOrUpdateAdminUserForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  dismissModal,
  setFieldValue,
}) => {
  const intl = useIntl();
  // roleLabelOf: efter ett rollbyte är values.role ett optionsobjekt, inte en sträng.
  const currentRole =
    values.roles.find((e) => e.label === roleLabelOf(values.role)) ??
    values.roles[0];

  // setFieldValue, inte direktmutation — annars renderas inte de villkorade
  // Analytics-fälten när rollen byts.
  const handleChange = (value) => {
    setFieldValue("role", value);
  };

  const isAnalytics = roleLabelOf(values.role) === "Analytics";

  return (
    <div>
      <Form>
        <FormGroup>
          <Label>
            <FormattedMessage id="Name" />
          </Label>
          <Field
            className={`form-control ${
              errors.name && touched.name && "is-invalid"
            }`}
            type="input"
            name="name"
            placeholder={intl.formatMessage({ id: "UsersName" })}
          />
          {errors.name && touched.name && (
            <div className="invalid-feedback">{errors.name}</div>
          )}
        </FormGroup>
        <FormGroup>
          <Label>
            <FormattedMessage id="EmailAddress" />
          </Label>
          <Field
            className={`form-control ${
              errors.email && touched.email && "is-invalid"
            }`}
            type="email"
            name="email"
            placeholder={intl.formatMessage({ id: "UsersEmailAddress" })}
          />
          {errors.email && touched.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
          {isAnalytics && (
            <p className="text-muted small mb-0 mt-1">
              <FormattedMessage id="AnalyticsEmailInfo" />
            </p>
          )}
        </FormGroup>
        <FormGroup>
          <Select
            placeholder={intl.formatMessage({ id: "ChooseRole" })}
            className={`${
              errors.selectedTeacherIds &&
              touched.selectedTeacherIds &&
              "is-invalid"
            }`}
            onChange={(selected) => handleChange(selected)}
            defaultValue={currentRole}
            options={values.roles}
          />
        </FormGroup>

        {isAnalytics && (
          <>
            <FormGroup>
              <Label>
                <FormattedMessage id="Password" />
              </Label>
              <div className="d-flex gap-2">
                <Field
                  className={`form-control ${
                    errors.password && touched.password && "is-invalid"
                  }`}
                  type="text"
                  name="password"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  color="primary"
                  outline
                  className="text-nowrap flex-shrink-0"
                  onClick={() => setFieldValue("password", generatePassword())}
                >
                  <FormattedMessage id="GeneratePassword" />
                </Button>
                <Button
                  type="button"
                  color="primary"
                  outline
                  className="flex-shrink-0"
                  disabled={!values.password}
                  title={intl.formatMessage({ id: "Copy" })}
                  onClick={() => copyToClipboard(values.password, intl)}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
              </div>
              {errors.password && touched.password && (
                <div className="invalid-feedback d-block">
                  {errors.password}
                </div>
              )}
              <p className="text-muted small mb-0 mt-1">
                <FormattedMessage id="AnalyticsPasswordHint" />
              </p>
              <p className="text-danger small mb-0 mt-1">
                <FormattedMessage id="AnalyticsPasswordWarning" />
              </p>
            </FormGroup>
          </>
        )}

        <FormGroup>
          <Label>Access token</Label>
          <Field
            className={`form-control ${
              errors.token && touched.token && "is-invalid"
            }`}
            type="input"
            name="token"
            placeholder="Access token"
          />
          {errors.token && touched.token && (
            <div className="invalid-feedback">{errors.token}</div>
          )}
        </FormGroup>

        <div className="d-flex justify-content-end gap-2">
          <Button color="secondary" outline onClick={dismissModal}>
            <FormattedMessage id="Cancel" />
          </Button>
          <Button color="primary" outline type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Sparar..." : "Spara"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues({ user }) {
    return {
      updateUser: user !== null,
      name: user ? user.name : "",
      email: user ? user.email : "",
      token: "",
      password: "",
      roles: [
        { label: "Admin", value: 3 },
        { label: "SuperUser", value: 2 },
        { label: "Analytics", value: 5 },
      ],
      role: user ? user.role : null,
    };
  },

  validationSchema: Yup.object().shape({
    // Bara tjänstekonton anger lösenord här — övriga sätter sitt eget via mailet.
    password: Yup.string()
      .test(
        "required-for-analytics",
        <FormattedMessage id="AnalyticsPasswordRequired" />,
        function (value) {
          return roleLabelOf(this.parent.role) !== "Analytics" || !!value;
        },
      )
      // Valideras här för att steg 1 annars hinner skapa raden innan auth avvisar
      // lösenordet i steg 2. Samma delade mönster som självregistreringen och
      // lösenordsbytet använder.
      .test(
        "policy-for-analytics",
        <FormattedMessage id="AnalyticsPasswordHint" />,
        function (value) {
          if (roleLabelOf(this.parent.role) !== "Analytics" || !value) {
            return true;
          }
          return PASSWORD_PATTERN.test(value);
        },
      ),
    email: Yup.string()
      .email(
        <FormattedMessage id="Views.Teacher.CreateAdminUser.Form.Error.EmailFormat" />,
      )
      .required(
        <FormattedMessage id="Views.Teacher.CreateAdminUser.Form.Error.RequireEmail" />,
      ),
    name: Yup.string().required(
      <FormattedMessage id="Views.Teacher.CreateAdminUser.Form.Error.RequireName" />,
    ),
    token: Yup.string().required(
      <FormattedMessage id="Views.Teacher.CreateAdminUser.Form.Error.RequireToken" />,
    ),
  }),

  handleSubmit(values, { props, resetForm, setErrors, setSubmitting }) {
    // Trimmas här: API:t trimmar själv på anropet, men auth-serverns tokenspärr jämför
    // exakt — ett inklistrat token med avslutande blanksteg passerar steg 1 och faller
    // i steg 2 med 404.
    var user = {
      name: values.name.trim(),
      email: values.email.trim(),
      validationToken: values.token.trim(),
    };

    if (values.role) {
      user.role = values.role.label ? values.role.label : values.role;
    } else if (props.updateUser) {
      user.role = props.user.role;
    } else {
      user.role = "Admin";
    }

    if (values.updateUser) {
      user.id = props.user.id;
      this.updateUser(user, props, resetForm, setErrors, setSubmitting);
    } else if (user.role === "Analytics") {
      this.createAnalyticsUser(user, values.password, props, setSubmitting);
    } else {
      this.createNewUser(user, props, resetForm, setErrors, setSubmitting);
    }
    setSubmitting(false);
  },

  createNewUser(user, props, resetForm, setErrors, setSubmitting) {
    axios
      .post("/user/createuser", user)
      .then((res) => {
        toast.success(<FormattedMessage id="UserAdded" />);
        props.updateUsers?.();
        setSubmitting(false);
        props.dismissModal();
      })
      .catch((err) => {
        // Backend svarar med MappedMessages-nycklar (t.ex. IdentifierAlreadyInUse).
        // String(err) gav bara "AxiosError: Request failed with status code 400".
        const message =
          formatServiceError(props.intl, err?.response?.data) || String(err);

        toast.warn(
          <FormattedMessage id="UserAddFailed" values={{ error: message }} />,
        );
        setSubmitting(false);
      });
  },

  // Tjänstekonto i två steg, samma som pensionerade New-AnalyticsAccount.ps1:
  // 1) raden i API-databasen (createuser), 2) Identity-kontot med lösenord och roll
  // (auth token/newuser). Failar steg 2 kan formuläret köras om: en befintlig rad som
  // redan är Analytics stoppar inte omkörningen, utan flödet fortsätter till steg 2.
  createAnalyticsUser(user, password, props, setSubmitting) {
    const fail = (message) => {
      toast.warn(
        <FormattedMessage id="UserAddFailed" values={{ error: message }} />,
      );
      setSubmitting(false);
    };

    const registerIdentity = () => {
      authAxios
        .post(`${AUTH_BASE_URL}/token/newuser`, {
          email: user.email,
          password: password,
          userType: "Analytics",
          validationToken: user.validationToken,
        })
        .then((res) => {
          const code = res.data?.code;
          if (code === "999") {
            toast.success(<FormattedMessage id="UserAdded" />);
            setSubmitting(false);
            props.dismissModal();
          } else if (code === "902") {
            fail(
              props.intl.formatMessage({
                id: "AnalyticsIdentityAlreadyExisted",
              }),
            );
          } else {
            // 901: auth-serverns message skiljer lösenordsregler från saknad rad
            fail(res.data?.message ?? code);
          }
        })
        .catch((err) => {
          // 404 = fel eller saknad access token (auth svarar NotFound på tokenspärren)
          fail(
            err?.response?.status === 404
              ? props.intl.formatMessage({
                  id: "Views.Teacher.CreateAdminUser.Form.Error.RequireToken",
                })
              : String(err),
          );
        });
    };

    const createRowThenRegister = () => {
      axios
        .post("/user/createuser", user)
        .then(() => {
          // Raden finns nu oavsett hur steg 2 går — visa den i listan direkt.
          props.updateUsers?.();
          registerIdentity();
        })
        .catch((err) => {
          const raw = String(err?.response?.data ?? "");

          if (err?.response?.status === 404) {
            fail(
              props.intl.formatMessage({
                id: "Views.Teacher.CreateAdminUser.Form.Error.RequireToken",
              }),
            );
          } else if (raw.includes("IdentifierAlreadyInUse")) {
            // Adressen finns redan i API-databasen. Är den ett Analytics-konto är det en
            // halvfärdig eller omkörd registrering — fortsätt till steg 2. Annars är det
            // en riktig konflikt (t.ex. en elev) och steg 2 får inte köras: den skulle ge
            // det kontot ett Analytics-Identity.
            axios
              .get(`/User/ByEmail?email=${encodeURIComponent(user.email)}`)
              .then((res) => {
                if (res.data?.result?.userType === "Analytics") {
                  registerIdentity();
                } else {
                  fail(formatServiceError(props.intl, raw) || raw);
                }
              })
              .catch(() => fail(formatServiceError(props.intl, raw) || raw));
          } else {
            fail(formatServiceError(props.intl, raw) || String(err));
          }
        });
    };

    // Förkontroll innan raden skapas: finns adressen bara på auth-sidan (t.ex. en
    // hårt raderad användare vars Identity blev kvar) skulle steg 1 lämna en
    // föräldralös rad vars namn sedan blockerar omförsök med annan adress.
    // IsEmailTaken svarar för båda databaserna, så en träff skickas vidare till
    // ByEmail som avgör vilken sida det är. api-instansen används för att
    // interceptorn ska sätta Bearer — endpointen kräver inloggad Superuser/Admin.
    axios
      .post(
        `${AUTH_BASE_URL}/token/IsEmailTaken?email=${encodeURIComponent(user.email)}`,
      )
      .then((res) => {
        if (res.data !== true) {
          createRowThenRegister();
          return;
        }

        axios
          .get(`/User/ByEmail?email=${encodeURIComponent(user.email)}`)
          .then((byEmail) => {
            if (byEmail.data?.result?.userType === "Analytics") {
              // Halvfärdig registrering — raden finns, hoppa direkt till steg 2.
              registerIdentity();
            } else {
              fail(props.intl.formatMessage({ id: "IdentifierAlreadyInUse" }));
            }
          })
          // 404 = adressen finns inte i API-databasen men är upptagen hos auth:
          // ingen rad skapas, alltså inget namn som fastnar.
          .catch(() =>
            fail(
              props.intl.formatMessage({
                id: "AnalyticsIdentityAlreadyExisted",
              }),
            ),
          );
      })
      // Kan förkontrollen inte nås gäller gamla flödet, som har egen felhantering.
      .catch(() => createRowThenRegister());
  },

  updateUser(user, resetForm, setErrors, setSubmitting) {
    console.warn("updateUser() => NOT IMPLEMENTED");
  },
})(CreateOrUpdateAdminUserForm);

// withFormik-konfigurationen kan inte använda hooks, och den här versionen av react-intl
// har ingen injectIntl. Wrappern skickar in intl som prop så handleSubmit kommer åt den.
const CreateOrUpdateAdminUserFormWithIntl = (props) => {
  const intl = useIntl();

  return <FormikForm {...props} intl={intl} />;
};

export default CreateOrUpdateAdminUserFormWithIntl;
